<template>
  <div class="complexity-slider-input">
    <!-- Mode switch: four dimension sliders or one overall slider -->
    <div class="complexity-mode-row">
      <div class="complexity-mode" role="group" :aria-label="wt('complexity', 'slider', 'complexityFactor')">
        <button
          v-for="mode in COMPLEXITY_MODES"
          :key="mode"
          type="button"
          class="complexity-mode-option"
          :class="{ 'is-active': activeMode === mode }"
          :aria-pressed="activeMode === mode"
          :data-testid="`complexity-mode-${mode}`"
          @click="setMode(mode)">
          {{ modeLabels[mode] }}
        </button>
      </div>
    </div>

    <!-- Height follows the active mode like the accordion; the new content fades in -->
    <div ref="modeContainer" class="complexity-mode-content" :style="{ '--complexity-label-width': labelColumnWidth }">
      <Transition name="complexity-mode-fade">
        <!-- Category Sliders -->
        <div v-if="activeMode === 'DIMENSIONS'" key="DIMENSIONS" class="space-y-2">
          <CategorySlider
            v-for="(category, index) in categories"
            :key="category.key"
            :modelValue="getSliderValue(category.key)"
            :values="segmentValues"
            :zoneLabels="zoneLabels"
            :showZoneLabels="index === categories.length - 1"
            :label="dimensionLabels[category.key]"
            :categoryKey="category.key"
            :hasJustification="hasJustification(category.key)"
            :isLocked="isSliderLocked(category.key)"
            :role="props.role"
            @update:modelValue="(val) => updateSliderValue(category.key, val)"
            @openJustification="(requested?: number) => openJustificationModal(category.key, requested)" />
        </div>

        <!-- Overall Slider -->
        <div v-else key="OVERALL">
          <CategorySlider
            :modelValue="modelValue?.sliderOverall ?? DEFAULT_SLIDER_VALUE"
            :values="segmentValues"
            :zoneLabels="zoneLabels"
            :label="overallLabel"
            categoryKey="overall"
            :justifiable="false"
            :role="props.role"
            @update:modelValue="updateOverallValue" />
        </div>
      </Transition>
    </div>

    <!-- Result: overall factor and the resulting min/m² -->
    <div class="complexity-result">
      <span class="font-medium leading-none text-neutral-700">{{
        wt('complexity', 'slider', 'complexityFactor')
      }}</span>
      <div class="flex items-center gap-2 tabular-nums leading-none">
        <span class="text-xl font-bold text-primary-default" data-testid="complexity-factor">
          {{ displayedFactor.toFixed(2) }}
        </span>
        <template v-if="resultMinutes != null">
          <span aria-hidden="true" class="text-neutral-550">→</span>
          <span class="font-medium text-neutral-700" data-testid="complexity-result-minutes">
            {{ Math.round(resultMinutes) }} {{ t('general.min_per_sq_m') }}
          </span>
        </template>
      </div>
    </div>

    <!-- Justification Modal -->
    <ComplexityJustificationModal
      :visible="modalVisible"
      :category="currentCategory"
      :categoryLabel="currentCategoryLabel"
      :justificationValue="currentJustificationValue"
      :role="props.role"
      @save="handleJustificationSave"
      @delete="handleJustificationDelete"
      @close="closeModal" />
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, nextTick, toRef, watch, type PropType } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  COMPLEXITY_MODES,
  COMPLEXITY_SEGMENT_ZONES,
  complexityDimensionMean,
  complexityFactorForSliders,
  snapToComplexitySegment,
  type ComplexityMode,
  type ComplexitySegmentZone,
} from '@arcexperts/core-types'
import type { UsageCorrection } from '@/schemas/general/types'
import { useWidgetText } from '@/helpers/useWidgetText'
import { qualitySegmentValues } from '@/helpers/quality-segments'
import { useContentStore } from '@/stores/content'
import { useCalculatorForRole, type Role } from '@/stores/projectCenter/useCalculatorForRole'
import CategorySlider from './CategorySlider.vue'
import ComplexityJustificationModal, {
  type ComplexityCategory,
} from '@/components/partials/modals/ComplexityJustificationModal.vue'

const DEFAULT_SLIDER_VALUE = 1.0

interface CategoryConfig {
  key: ComplexityCategory
  sliderField: 'sliderCreative' | 'sliderTechnical' | 'sliderSpatial' | 'sliderOrganizational'
  justificationField:
    | 'justificationCreative'
    | 'justificationTechnical'
    | 'justificationSpatial'
    | 'justificationOrganizational'
  legacyProblemField: 'problem1' | 'problem2' | 'problem3' | 'problem4'
}

const categories: CategoryConfig[] = [
  {
    key: 'creative',
    sliderField: 'sliderCreative',
    justificationField: 'justificationCreative',
    legacyProblemField: 'problem1',
  },
  {
    key: 'technical',
    sliderField: 'sliderTechnical',
    justificationField: 'justificationTechnical',
    legacyProblemField: 'problem2',
  },
  {
    key: 'spatial',
    sliderField: 'sliderSpatial',
    justificationField: 'justificationSpatial',
    legacyProblemField: 'problem3',
  },
  {
    key: 'organizational',
    sliderField: 'sliderOrganizational',
    justificationField: 'justificationOrganizational',
    legacyProblemField: 'problem4',
  },
]

const modelValue = defineModel({ type: Object as PropType<UsageCorrection> })
const props = defineProps<{
  usageIndex: number
  role?: Role
  /** Final min/m² of the usage from the calculation. */
  resultMinutes?: number
  /** Factor of a legacy override that still applies (project did not accept the segment update yet). */
  legacyFactor?: number | null
}>()
const { wt } = useWidgetText('widget-texts-project-setup', toRef(props, 'role'))
const { t } = useI18n()
const contentStore = useContentStore()

const setupCalculator = useCalculatorForRole(toRef(props, 'role'))

// Modal state
const modalVisible = ref(false)
const currentCategory = ref<ComplexityCategory>('creative')
/** Segment value the user clicked on a locked bar; it applies once the justification is saved. */
const requestedValue = ref<number | null>(null)

const currentCategoryConfig = computed(() => categories.find((c) => c.key === currentCategory.value) || categories[0])

const currentCategoryLabel = computed(() => wt('complexity', 'dimensions', currentCategory.value, 'title'))

const dimensionLabels = computed<Record<ComplexityCategory, string>>(() => ({
  creative: wt('complexity', 'dimensions', 'creative', 'title'),
  technical: wt('complexity', 'dimensions', 'technical', 'title'),
  spatial: wt('complexity', 'dimensions', 'spatial', 'title'),
  organizational: wt('complexity', 'dimensions', 'organizational', 'title'),
}))

/** Payload text, or the widget default while it is empty. */
function complexityText(path: string[], fallbackKey: string): string {
  return wt('complexity', ...path) || t(`project.setup.sections.complexity.${fallbackKey}`)
}

const segmentValues = computed(() =>
  qualitySegmentValues(contentStore.getComplexityStepSize(`${(props.role ?? 'ARCHITECT').toLowerCase()}_setup`))
)

const zoneLabels = computed(
  () =>
    Object.fromEntries(
      COMPLEXITY_SEGMENT_ZONES.map((zone) => [zone, complexityText(['segments', zone], `segments.${zone}`)])
    ) as Record<ComplexitySegmentZone, string>
)

const modeLabels = computed<Record<ComplexityMode, string>>(() => ({
  DIMENSIONS: complexityText(['mode', 'dimensions'], 'mode.dimensions'),
  OVERALL: complexityText(['mode', 'overall'], 'mode.overall'),
}))

const overallLabel = computed(() => complexityText(['overallSlider', 'title'], 'overall_slider'))

const activeMode = computed<ComplexityMode>(() =>
  modelValue.value?.complexityMode === 'OVERALL' ? 'OVERALL' : 'DIMENSIONS'
)

const labelColumnWidth = computed(() => {
  const longestLabelLength = Math.max(
    ...[...Object.values(dimensionLabels.value), overallLabel.value].map((label) => label.length)
  )
  const widthInCh = Math.max(longestLabelLength + 1, 6)
  return `min(${widthInCh}ch, 16rem)`
})

const currentJustificationValue = computed(() => {
  if (!modelValue.value) return null
  return modelValue.value[currentCategoryConfig.value.justificationField] || null
})

// Get slider value for a category
function getSliderValue(categoryKey: ComplexityCategory): number {
  if (!modelValue.value) return DEFAULT_SLIDER_VALUE
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return DEFAULT_SLIDER_VALUE
  return modelValue.value[config.sliderField] ?? DEFAULT_SLIDER_VALUE
}

/** Applies a change; the VAL-156 factor override and a legacy final min/m² value stop applying with any edit here. */
function applyChange(change: Partial<UsageCorrection>) {
  if (!modelValue.value) return
  modelValue.value = {
    ...modelValue.value,
    complexityFactorOverride: null,
    manualComplexity: null,
    ...change,
  }
  setupCalculator.value.setComplexityForUsages()
}

// Update slider value
function updateSliderValue(categoryKey: ComplexityCategory, value: number) {
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return
  applyChange({ [config.sliderField]: value })
}

function updateOverallValue(value: number) {
  applyChange({ sliderOverall: value })
}

/** The first switch to the overall slider starts at the segment closest to the dimension mean. */
function setMode(mode: ComplexityMode) {
  if (!modelValue.value || mode === activeMode.value) return
  const sliderOverall =
    mode === 'OVERALL' && modelValue.value.sliderOverall == null
      ? snapToComplexitySegment(segmentValues.value, complexityDimensionMean(modelValue.value))
      : modelValue.value.sliderOverall
  applyChange({ complexityMode: mode, sliderOverall })
}

// Check if category has justification (field must have at least 10 characters)
function hasJustification(categoryKey: ComplexityCategory): boolean {
  if (!modelValue.value) return false
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return false
  const justification = modelValue.value[config.justificationField]
  return !!(justification && justification.length >= 10)
}

// Check if slider is locked (no justification for non-default values)
function isSliderLocked(categoryKey: ComplexityCategory): boolean {
  if (!modelValue.value) return true
  const config = categories.find((c) => c.key === categoryKey)
  if (!config) return true

  // Slider is locked if no justification
  return !hasJustification(categoryKey)
}

// Overall quality adjustment factor, rounded to 0.05 in both modes
const complexityFactor = computed(() =>
  modelValue.value ? complexityFactorForSliders(modelValue.value) : DEFAULT_SLIDER_VALUE
)
const displayedFactor = computed(() => props.legacyFactor ?? complexityFactor.value)

// Mode switch: animate the container height from the old to the new content (accordion timing).
const modeContainer = ref<HTMLElement | null>(null)
let modeTransitionTimer: ReturnType<typeof setTimeout> | undefined
watch(activeMode, async () => {
  const container = modeContainer.value
  if (!container) return
  clearTimeout(modeTransitionTimer)
  container.style.height = `${container.offsetHeight}px`
  await nextTick()
  const entering = container.querySelector<HTMLElement>(':scope > :not(.complexity-mode-fade-leave-active)')
  const target = entering?.offsetHeight ?? container.scrollHeight
  requestAnimationFrame(() => {
    container.style.height = `${target}px`
  })
  modeTransitionTimer = setTimeout(() => {
    container.style.height = ''
  }, 260)
})

// Modal handlers
function openJustificationModal(categoryKey: ComplexityCategory, requested?: number) {
  currentCategory.value = categoryKey
  requestedValue.value = requested !== undefined && requested !== getSliderValue(categoryKey) ? requested : null
  modalVisible.value = true
}

function closeModal() {
  modalVisible.value = false
  requestedValue.value = null
}

/** Saves the justification and applies the segment the user had clicked on the locked bar. */
function handleJustificationSave(justification: string) {
  if (!modelValue.value) return

  const config = currentCategoryConfig.value
  if (requestedValue.value !== null) {
    applyChange({ [config.justificationField]: justification, [config.sliderField]: requestedValue.value })
  } else {
    modelValue.value = {
      ...modelValue.value,
      [config.justificationField]: justification,
    }
  }
  closeModal()
}

function handleJustificationDelete() {
  if (!modelValue.value) return

  const config = currentCategoryConfig.value

  applyChange({
    [config.justificationField]: null,
    [config.sliderField]: DEFAULT_SLIDER_VALUE,
  })
  closeModal()
}
</script>

<style scoped>
.complexity-slider-input {
  @apply bg-white overflow-visible;
}

.complexity-mode-row {
  @apply mb-4 flex justify-end;
}

.complexity-mode {
  @apply inline-flex border border-neutral-300;
}

.complexity-mode-content {
  @apply relative overflow-hidden;
  transition: height 220ms cubic-bezier(0.42, 0, 0.58, 1);
}

.complexity-mode-fade-enter-active {
  transition: opacity 220ms cubic-bezier(0.42, 0, 0.58, 1);
}

.complexity-mode-fade-enter-from {
  opacity: 0;
}

/* The old content leaves at once; it is taken out of the flow so the height can animate. */
.complexity-mode-fade-leave-active {
  @apply absolute inset-x-0 top-0;
  transition: opacity 120ms ease-out;
}

.complexity-mode-fade-leave-to {
  opacity: 0;
}

.complexity-result {
  @apply mt-6 flex items-center justify-between gap-4 border border-neutral-300 bg-neutral-35 px-5 py-4;
}

.complexity-mode-option {
  @apply px-4 py-1.5 text-sm text-neutral-700 transition-colors hover:bg-neutral-100;
}

.complexity-mode-option + .complexity-mode-option {
  @apply border-l border-neutral-300;
}

.complexity-mode-option.is-active {
  @apply bg-primary-default text-white hover:bg-primary-default;
}
</style>
